import React, { useState } from 'react';
import { useToast } from '../ui/Toast';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { formatDate } from '../../utils/formatPreferences';
import { STORAGE_KEYS } from '../../constants';
import './SecuritySettings.css';

/**
 * Security Settings Component
 * Configuration for security and authentication settings
 */
const SecuritySettings = () => {
  const { showToast } = useToast();
  const { user } = useAdminAuth();
  
  // Get admin email and create safe key
  const getAdminEmailKey = () => {
    const adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
    return adminEmail.replace(/@/g, '_').replace(/\./g, '_');
  };

  const emailKey = getAdminEmailKey();
  
  // Load preferences from localStorage
  const loadPreference = (key, defaultValue) => {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    return stored === 'true';
  };

  const loadNumberPreference = (key, defaultValue) => {
    const stored = localStorage.getItem(key);
    if (stored === null) return defaultValue;
    const num = parseInt(stored);
    return isNaN(num) ? defaultValue : num;
  };

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(
    loadPreference(`${emailKey}_security_2fa`, false)
  );
  const [sessionTimeout, setSessionTimeout] = useState(
    loadNumberPreference(`${emailKey}_security_session_timeout`, 30)
  );
  const [requirePasswordChange, setRequirePasswordChange] = useState(
    loadPreference(`${emailKey}_security_require_password_change`, false)
  );
  
  const savePreference = (key, value) => {
    localStorage.setItem(key, value.toString());
  };

  const handleTwoFactorChange = (value) => {
    setTwoFactorEnabled(value);
    savePreference(`${emailKey}_security_2fa`, value);
    showToast(`Two-factor authentication ${value ? 'enabled' : 'disabled'}`, value ? 'success' : 'warning');
  };

  const handleSessionTimeoutChange = (value) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 5 && numValue <= 120) {
      setSessionTimeout(numValue);
      savePreference(`${emailKey}_security_session_timeout`, numValue);
      showToast(`Session timeout set to ${numValue} minutes`, 'success');
    }
  };

  const handleRequirePasswordChange = (value) => {
    setRequirePasswordChange(value);
    savePreference(`${emailKey}_security_require_password_change`, value);
    showToast(`Password change requirement ${value ? 'enabled' : 'disabled'}`, 'success');
  };

  const handleChangePassword = () => {
    showToast('Password change feature coming soon', 'info');
  };

  const handleViewActivityLog = () => {
    showToast('Activity log feature coming soon', 'info');
  };

  return (
    <div className="security-settings admin-card">
      <h3 className="section-title">
        <i className="bi bi-shield-lock icon-brand" style={{ marginRight: 8 }}></i>
        Security Settings
      </h3>

      {/* Current User Info */}
      <div className="user-info">
        <div className="info-item">
          <span className="info-label">Logged in as:</span>
          <span className="info-value">{user?.email || 'Admin User'}</span>
        </div>
        <div className="info-item">
          <span className="info-label">Last login:</span>
          <span className="info-value">
            {formatDate(new Date())}
          </span>
        </div>
      </div>

      <div className="settings-group">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Two-Factor Authentication</h4>
            <p>Add an extra layer of security to your account</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={twoFactorEnabled}
              onChange={(e) => handleTwoFactorChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Session Timeout</h4>
            <p>Automatic logout after inactivity (in minutes)</p>
          </div>
          <div className="input-group">
            <input
              type="number"
              min="5"
              max="120"
              value={sessionTimeout}
              onChange={(e) => handleSessionTimeoutChange(e.target.value)}
              className="number-input"
            />
            <span className="input-suffix">minutes</span>
          </div>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Require Password Change</h4>
            <p>Force password change on next login</p>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={requirePasswordChange}
              onChange={(e) => handleRequirePasswordChange(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      <div className="settings-actions">
        <button 
          className="btn-primary"
          onClick={handleChangePassword}
        >
          <i className="bi bi-key icon-brand"></i>
          Change Password
        </button>
        <button 
          className="btn-secondary"
          onClick={handleViewActivityLog}
        >
          <i className="bi bi-clock-history icon-brand"></i>
          View Activity Log
        </button>
      </div>
    </div>
  );
};

export default SecuritySettings;
